// PHASE_2 / B0 — TR payroll feature API helpers (READ-only reference slice).
//
// The TR payroll read path the iston employee-payslip-v2 shell consumes:
//   GET /v1/payroll-tr/runs/:runId/statements      — keyset list (the period's statements)
//   GET /v1/payroll-tr/runs/:runId/statements/:id  — one statement (metadata)
//
// PDF-ONLY per KVKK (gap analysis §5.8; review gate item 9): the employee
// surface renders period metadata + a PDF-download CTA, NOT line items. The
// statement read returns gross/net minor-units for the admin/manager preview;
// the employee Content does not display the breakdown.
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
//
// Money crosses the edge as minor-units integer STRINGS — the <Money> component
// is the ONLY place it reassembles into a localized currency string.

import type { PayStatementTr, SeveranceAssessment } from "@novemai/payroll-tr-sdk";

import { getAccessToken, platformGraph, platformGraphBaseUrl } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";

/** A TR pay-statement row (documented shape from routes/payroll-tr.ts). */
export interface PayStatementRow {
  readonly pay_statement_id: string;
  readonly tenant_id: string;
  readonly pay_run_id: string;
  readonly employee_id: string;
  readonly gross_total_minor_units: string;
  readonly net_pay_minor_units: string;
  readonly currency: string;
  /** The rendered PDF document id (B4); null until the payslip is generated. */
  readonly pdf_document_id: string | null;
  readonly calculated_at: string | null;
}

type GetStatements = (
  path: "/v1/payroll-tr/runs/{runId}/statements",
  init: {
    params: { path: { runId: string }; query: { cursor?: string; mine?: "true" } };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<PayStatementRow>>>;

type GetStatement = (
  path: "/v1/payroll-tr/runs/{runId}/statements/{id}",
  init: { params: { path: { runId: string; id: string } } },
) => Promise<EnvelopedFetchResult<{ statement: PayStatementRow }>>;

/**
 * One keyset page of a run's statements.
 *
 * `mine` is what makes the SELF surface self: the server narrows an
 * employee_self/manager caller on its own, but an executive_broad caller gets
 * the whole run — correct for the payroll-admin screen, and wrong for
 * /me/payroll/:runId, which is titled "my payslips".
 */
export async function fetchRunStatementsPage(
  runId: string,
  cursor: string | null,
  mine = false,
): Promise<KeysetPage<PayStatementRow>> {
  const get = platformGraph.GET as unknown as GetStatements;
  return unwrapEnvelope(
    get("/v1/payroll-tr/runs/{runId}/statements", {
      params: {
        path: { runId },
        query: {
          ...(cursor !== null && { cursor }),
          ...(mine && { mine: "true" as const }),
        },
      },
    }),
  );
}

/**
 * One FINALIZED period in the caller's OWN payslip history (the statement
 * JOINed to its run's period metadata — repo listEmployeePeriods).
 *
 * snake_case, matching the observed wire payload. The repo SELECT names these
 * in camelCase and a serializer renames them on the way out, so typing this
 * from the repo is wrong — and the `as unknown as` cast these helpers use to
 * stand in for the missing schema block means the compiler cannot say so.
 * Verified against the live response, not the query.
 */
export interface MyPeriodRow {
  readonly pay_statement_id: string;
  readonly pay_run_id: string;
  readonly period_label: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly run_kind: string;
  readonly state: string;
  readonly gross_total_minor_units: string;
  readonly net_pay_minor_units: string;
  readonly currency: string;
}

type GetMyPeriods = (
  path: "/v1/payroll-tr/me/statements",
  init: { params: { query: { cursor?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<MyPeriodRow>>>;

/**
 * The caller's own finalized periods, newest period first (the repo seeks
 * DESC on (period_start, pay_statement_id)). The self boundary is the JWT scope, never a query param — an
 * `employee_id` sent from here would be IGNORED for a non-exec caller, so this
 * client deliberately does not offer one.
 */
export async function fetchMyPeriodsPage(
  cursor: string | null,
): Promise<KeysetPage<MyPeriodRow>> {
  const get = platformGraph.GET as unknown as GetMyPeriods;
  return unwrapEnvelope(
    get("/v1/payroll-tr/me/statements", {
      params: { query: cursor !== null ? { cursor } : {} },
    }),
  );
}

/** Fetch one statement's metadata (used by the read-statement detail). */
export async function fetchStatement(
  runId: string,
  id: string,
): Promise<PayStatementRow> {
  const get = platformGraph.GET as unknown as GetStatement;
  const result = await unwrapEnvelope(
    get("/v1/payroll-tr/runs/{runId}/statements/{id}", {
      params: { path: { runId, id } },
    }),
  );
  return result.statement;
}

// ===========================================================================
// PHASE_2 / B4-B5 — payroll-ADMIN write path (executive_broad only server-side).
// The BulkPayrollWizard, payslip-preview, and exit/severance screens consume
// these. Explicit adapter signatures retain the consumed wire contracts.
// Money crosses as minor-units STRINGS; the
// <Money> component is the ONLY place a figure reassembles into a localized
// currency string.
// ===========================================================================

/** A pay-run row (the ADR-009 subject; the period's run state + totals). */
export interface PayRunRow {
  readonly pay_run_id: string;
  readonly tenant_id: string;
  /** The legal employer pinned by the run, independent of the viewer's company context. */
  readonly legal_entity_id?: string | null;
  readonly period_label: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly run_kind: string;
  readonly state: string;
  readonly statement_count: number;
  readonly gross_total_minor_units: string | null;
  readonly net_total_minor_units: string | null;
  /** Brüt + işveren SGK payı − destekler (motor: calculate_total_employer_cost); "SGK primi" toplamı değildir. */
  readonly employer_cost_total_minor_units?: string | null;
  readonly currency: string | null;
  readonly job_id: string | null;
  /** W3: the platform-approvals request routing this run's maker-checker
      sign-off (spec 69:349-352); null until submitted for approval. */
  readonly approval_request_id: string | null;
}

/**
 * Polling gate for a run's own GET query (SEM-4153 Part B; extended SEM-4154
 * / Faz 4). Both `calculate` and `finalize` are now always-async 202 write
 * paths — the run sits in `calculating` until the calc chunk chain completes,
 * or in `finalizing` until the payslip-render job completes. Without polling
 * the UI would show the in-flight state until the admin manually reloads.
 * Returns a TanStack Query `refetchInterval` value: a millisecond interval
 * while calculating OR finalizing, `false` (no polling) otherwise — every
 * other state is either not-yet-started or already terminal-for-display, so
 * polling would just be wasted requests.
 */
export function payrollRunRefetchIntervalMs(run: Pick<PayRunRow, "state"> | null | undefined): number | false {
  return run?.state === "calculating" || run?.state === "finalizing" ? 3000 : false;
}

/**
 * How long the screen keeps Calculate locked while a run reads `calculating`
 * before it offers a retry. Mirrors the server's calculation lease (5 minutes,
 * renewed by the running job): past it, a run that has not moved is either a
 * crashed job the server will reclaim on the next calculate, or a very long one
 * whose live lease makes that calculate answer `run_state_invalid`. The run row
 * carries no lease time, so the clock is "this screen has watched it that long".
 */
export const CALCULATION_STALL_AFTER_MS = 5 * 60_000;

/** One named-earning line on a statement (admin payslip-preview). */
export interface EarningLineRow {
  readonly earning_component_id: string;
  readonly code: string;
  readonly label_key: string;
  readonly sort_order: number;
  /** 0,5 steps — a half UBGT day (arife, 28 Ekim). */
  readonly quantity_days: number | null;
  readonly quantity_hours_decimal: string | null;
  readonly amount_minor_units: string;
  readonly currency: string;
}

/** One deduction line on a statement (admin payslip-preview). */
export interface DeductionLineRow {
  readonly deduction_component_id: string;
  readonly code: string;
  readonly label_key: string;
  readonly sort_order: number;
  readonly kind: string;
  readonly amount_minor_units: string;
  readonly applied_amount_minor_units: string | null;
  readonly currency: string;
}

/** One employee's calc inputs the wizard submits (entered/imported). */
export interface EmployeeCalcInput {
  readonly employee_id: string;
  readonly gross_salary_minor_units: string;
  readonly meal?: { readonly amount_minor_units: string; readonly type?: string };
  readonly worked_days?: number;
  readonly earnings?: ReadonlyArray<{
    readonly code: string;
    readonly label_key: string;
    readonly amount_minor_units: string;
    readonly sgk_taxable: boolean;
    readonly income_tax_taxable: boolean;
    readonly stamp_tax_taxable: boolean;
  }>;
  readonly deductions?: ReadonlyArray<{
    readonly code: string;
    readonly label_key: string;
    readonly amount_minor_units: string;
    readonly kind: "tax_base_reduction" | "post_tax_legal" | "voluntary";
    readonly iik_priority?: number;
    readonly legal_cap_ratio?: number;
  }>;
}

export interface CreateRunInput {
  readonly legal_entity_id?: string;
  readonly period_label: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly run_kind?: "regular" | "exit" | "correction" | "supplemental";
  readonly currency?: string;
}

export interface CalculateRunResult {
  readonly run: PayRunRow | null;
  readonly processed_count?: number;
  readonly skipped_count?: number;
  readonly mode: "sync" | "async";
  readonly job_id?: string;
  readonly run_id?: string;
}

/** SEM-4154 / Faz 4: finalize is now ALWAYS async — the response is the
 *  same 202-style envelope shape the calculate route's async branch already
 *  uses (run_id + job_id + mode), never a synchronous run/payslips_rendered
 *  body. The run transitions approved -> finalizing immediately; the caller
 *  polls (payrollRunRefetchIntervalMs) until it reaches finalized. */
export interface FinalizeRunResult {
  readonly run_id: string;
  readonly job_id: string;
  readonly mode: "async";
}

/** Immutable C1 bridge batch built from locked TR_4857 timesheets. */
export interface TimePayrollExportInput {
  readonly period_start: string;
  readonly period_end: string;
  readonly legal_entity_id?: string;
}

/** The batch handle payroll-tr must receive as `export_batch_id` exactly. */
export interface TimePayrollExportResult {
  readonly batch_id: string;
  readonly timesheet_count: number;
  readonly revision: number;
}

/**
 * Süre GÖNDERİLMEZ (SEM-11201): sunucu kıdem başlangıcı ile fesih tarihinden
 * kendisi hesaplar ve gövde strict olduğu için `tenure_days` taşıyan bir istek
 * 400 alır.
 */
export interface ExitStatementInput {
  readonly employee_id: string;
  readonly reason: "istifa" | "fesih" | "karsilikli" | "emeklilik" | "vefat";
  readonly assessment: SeveranceAssessment;
  readonly monthly_gross_minor_units: string;
  readonly monthly_meal_minor_units?: string;
  readonly monthly_transport_minor_units?: string;
  readonly monthly_regular_benefits_minor_units?: string;
  readonly contractual_notice_days?: number;
  readonly non_service_days?: number;
  readonly non_service_reason?: string;
}

/**
 * One person a run pays, with the monthly base pay on record for them
 * (GET /v1/payroll-tr/runs/:id/roster). `base_salary_minor_units` is null when
 * no pay fact the run can use exists — the screen then asks the admin to enter
 * it instead of guessing one.
 */
export interface RunRosterRow {
  readonly employee_id: string;
  readonly employee_number: string | null;
  readonly legal_first_name: string;
  readonly legal_last_name: string;
  readonly employment_id: string;
  readonly base_salary_minor_units: string | null;
}

export interface RunRoster {
  readonly currency: string;
  readonly items: readonly RunRosterRow[];
}

type GetRoster = (
  path: "/v1/payroll-tr/runs/{id}/roster",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<RunRoster>>;

type PostCreateRun = (
  path: "/v1/payroll-tr/runs",
  init: { body: CreateRunInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ run: PayRunRow }>>;

type PostCalculate = (
  path: "/v1/payroll-tr/runs/{id}/calculate",
  init: {
    params: { path: { id: string } };
    body: { export_batch_id?: string; employees: readonly EmployeeCalcInput[] };
  },
) => Promise<EnvelopedFetchResult<CalculateRunResult>>;

type PostTimePayrollExport = (
  path: "/v1/time/payroll/exports",
  init: { body: TimePayrollExportInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<TimePayrollExportResult>>;

type PostFinalize = (
  path: "/v1/payroll-tr/runs/{id}/finalize",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<FinalizeRunResult>>;

type PostSubmitRunForApproval = (
  path: "/v1/payroll-tr/runs/{id}/approve",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ run: PayRunRow }>>;

type GetRun = (
  path: "/v1/payroll-tr/runs/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ run: PayRunRow }>>;

type ListRuns = (
  path: "/v1/payroll-tr/runs",
  init: { params: { query: { limit?: number; cursor?: string } } },
) => Promise<EnvelopedFetchResult<{ items: readonly PayRunRow[]; nextCursor: string | null }>>;

type GetLines = (
  path: "/v1/payroll-tr/runs/{id}/statements/{sid}/lines",
  init: { params: { path: { id: string; sid: string } } },
) => Promise<
  EnvelopedFetchResult<{
    statement: PayStatementTr;
    earnings: readonly EarningLineRow[];
    deductions: readonly DeductionLineRow[];
  }>
>;

type PostExit = (
  path: "/v1/payroll-tr/runs/{id}/exit-statement",
  init: {
    params: { path: { id: string } };
    body: ExitStatementInput;
    headers: Record<string, string>;
  },
) => Promise<
  EnvelopedFetchResult<{ statement: PayStatementRow; severance: Record<string, unknown> }>
>;

/**
 * The caller owns this key because it alone can distinguish a retry of one
 * logical run-create from a later, intentionally new payroll run.
 */
export async function createPayRun(
  input: CreateRunInput,
  idempotencyKey: string,
): Promise<PayRunRow> {
  const post = platformGraph.POST as unknown as PostCreateRun;
  const result = await unwrapEnvelope(
    post("/v1/payroll-tr/runs", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.run;
}

/**
 * Build the locked-timesheet payroll export. The caller owns `idempotencyKey`
 * across an ambiguous response, because it alone knows whether a later click
 * is a retry of this exact period export or a new payroll intent.
 */
export async function createTimePayrollExport(
  input: TimePayrollExportInput,
  idempotencyKey: string,
): Promise<TimePayrollExportResult> {
  const post = platformGraph.POST as unknown as PostTimePayrollExport;
  return unwrapEnvelope(
    post("/v1/time/payroll/exports", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * `exportBatchId` is optional because it is optional on the route
 * (`CalculateRunSchema.export_batch_id`): the batch exists to pull locked
 * TR_4857 overtime into the calculation, and a month without any locked
 * timesheet is an ordinary salaried payroll, not an error. The key is omitted
 * rather than sent as `undefined` so the request body matches the schema.
 */
export async function calculatePayRun(
  id: string,
  employees: readonly EmployeeCalcInput[],
  exportBatchId?: string,
): Promise<CalculateRunResult> {
  const post = platformGraph.POST as unknown as PostCalculate;
  return unwrapEnvelope(
    post("/v1/payroll-tr/runs/{id}/calculate", {
      params: { path: { id } },
      body: {
        ...(exportBatchId !== undefined && { export_batch_id: exportBatchId }),
        employees,
      },
    }),
  );
}

/**
 * Submit a previewed run to the server-owned maker-checker chain. This does
 * not approve the run: the returned row is `awaiting_approval` until a
 * distinct approver records a generic approval decision.
 */
export async function submitPayRunForApproval(id: string): Promise<PayRunRow> {
  const post = platformGraph.POST as unknown as PostSubmitRunForApproval;
  const result = await unwrapEnvelope(
    post("/v1/payroll-tr/runs/{id}/approve", { params: { path: { id } } }),
  );
  return result.run;
}

export async function finalizePayRun(id: string): Promise<FinalizeRunResult> {
  const post = platformGraph.POST as unknown as PostFinalize;
  return unwrapEnvelope(post("/v1/payroll-tr/runs/{id}/finalize", { params: { path: { id } } }));
}

export async function fetchPayRun(id: string): Promise<PayRunRow> {
  const get = platformGraph.GET as unknown as GetRun;
  const result = await unwrapEnvelope(get("/v1/payroll-tr/runs/{id}", { params: { path: { id } } }));
  return result.run;
}

/**
 * One keyset page of the tenant's pay runs, newest first. The screen had no
 * way to reach an existing period before this — only the run it had just
 * created, held in local state.
 */
export async function fetchPayRunsPage(
  cursor: string | null,
): Promise<{ items: readonly PayRunRow[]; nextCursor: string | null }> {
  const get = platformGraph.GET as unknown as ListRuns;
  return unwrapEnvelope(
    get("/v1/payroll-tr/runs", {
      params: { query: { ...(cursor !== null && { cursor }) } },
    }),
  );
}

/**
 * The people this run should pay and the monthly base pay recorded for each.
 * The admin can edit every figure before calculating: calculate stays the one
 * writer and still takes an explicit gross per employee.
 */
export async function fetchRunRoster(id: string): Promise<RunRoster> {
  const get = platformGraph.GET as unknown as GetRoster;
  return unwrapEnvelope(get("/v1/payroll-tr/runs/{id}/roster", { params: { path: { id } } }));
}

export async function fetchStatementLines(
  runId: string,
  sid: string,
): Promise<{
  statement: PayStatementTr;
  earnings: readonly EarningLineRow[];
  deductions: readonly DeductionLineRow[];
}> {
  const get = platformGraph.GET as unknown as GetLines;
  return unwrapEnvelope(
    get("/v1/payroll-tr/runs/{id}/statements/{sid}/lines", {
      params: { path: { id: runId, sid } },
    }),
  );
}

export async function createExitStatement(
  runId: string,
  input: ExitStatementInput,
  idempotencyKey: string,
): Promise<{ statement: PayStatementRow; severance: Record<string, unknown> }> {
  const post = platformGraph.POST as unknown as PostExit;
  return unwrapEnvelope(
    post("/v1/payroll-tr/runs/{id}/exit-statement", {
      params: { path: { id: runId } },
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * Download the caller's own payslip PDF for one statement.
 *
 * The route serves verified retained bytes or explicitly regenerated legacy
 * output. The server checks current authorization and finalized run state;
 * pdf_document_id is not a client-side readiness signal.
 *
 * Not routed through the openapi-fetch client: that one unwraps the JSON
 * envelope, and this response is a raw PDF body. Mirrors `downloadIcs`.
 */
export async function downloadPayslipPdf(
  statementId: string,
  filename: string,
): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = { accept: "application/pdf" };
  if (token !== null && token !== "") headers["authorization"] = `Bearer ${token}`;
  const response = await fetch(
    `${platformGraphBaseUrl}/v1/payroll-tr/me/statements/${encodeURIComponent(statementId)}/pdf`,
    { method: "GET", headers, credentials: "include" },
  );
  if (!response.ok) {
    throw new Error(`payslip_pdf_failed:${response.status}`);
  }
  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Parse a TL-decimal string an admin types into an integer kuruş (minor-units)
 * string for the API body. The ONLY TL→minor edge in the payroll feature (the
 * <Money> component owns the inverse minor→display direction). Non-numeric /
 * out-of-format input collapses to "0"; up to 2 fractional digits are kept.
 */
export function tlToMinor(tl: string): string {
  const trimmed = tl.trim();
  if (!isTlAmount(trimmed)) return "0";
  const norm = trimmed.replace(",", ".");
  const [intPart, fracRaw = ""] = norm.split(".");
  const frac = fracRaw.padEnd(2, "0").slice(0, 2);
  return `${intPart}${frac}`.replace(/^0+(?=\d)/, "");
}

/**
 * The format `tlToMinor` accepts. Exported so a form can refuse input the edge
 * converter would silently turn into "0" (a zero gross that reaches the server
 * looks like a valid figure, not an error).
 */
export function isTlAmount(tl: string): boolean {
  return /^\d+([.,]\d{1,2})?$/.test(tl.trim());
}

/**
 * The inverse edge of `tlToMinor`, for prefilling an input from a stored figure
 * ("5000000" → "50000", "550" → "5.50"). Pure string math: minor units are
 * integers and a float round trip is exactly what the convention forbids.
 */
export function minorToTl(minor: string): string {
  if (!/^\d+$/.test(minor)) return "";
  const padded = minor.padStart(3, "0");
  const whole = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const fraction = padded.slice(-2);
  return fraction === "00" ? whole : `${whole}.${fraction}`;
}
